/**
 * Shared backoff when the API is unreachable (connection refused, network error).
 * Stops poll storms and ERR_CONNECTION_REFUSED console spam when the server is down.
 */

const INITIAL_BACKOFF_MS = 5_000;
const MAX_BACKOFF_MS = 60_000;

let backoffUntil = 0;
let consecutiveFailures = 0;

export class MiniappApiDeferredError extends Error {
  constructor() {
    super('API requests deferred — server unreachable');
    this.name = 'MiniappApiDeferredError';
  }
}

export function isMiniappApiDeferredError(error: unknown): boolean {
  return error instanceof MiniappApiDeferredError || (error as Error)?.name === 'MiniappApiDeferredError';
}

export function shouldDeferApiRequest(): boolean {
  return Date.now() < backoffUntil;
}

export function getApiBackoffRemainingMs(): number {
  return Math.max(0, backoffUntil - Date.now());
}

export function noteApiReachable(): void {
  consecutiveFailures = 0;
  backoffUntil = 0;
}

export function noteApiUnreachable(): void {
  consecutiveFailures += 1;
  const delay = Math.min(
    MAX_BACKOFF_MS,
    INITIAL_BACKOFF_MS * Math.pow(1.5, Math.max(0, consecutiveFailures - 1)),
  );
  backoffUntil = Date.now() + delay;
}

/** Force deferral until a specific time (e.g. health-check retry schedule). */
export function deferApiRequestsUntil(untilMs: number): void {
  if (!Number.isFinite(untilMs)) return;
  backoffUntil = Math.max(backoffUntil, untilMs);
}
